import { useRef, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { seekToSeconds } from '@/features/player/playerSlice';
import { setTranscriptSearch } from '@/features/ui/uiSlice';
import { fetchAssetTags, addAssetTag, deleteAssetTag } from '@/features/tags/tagsSlice';
import { transcriptLines as defaultTranscriptLines } from '@/data/mockData';
import { formatTimestamp } from '@/utils/formatters';
import type { TranscriptLine, TagType, TagOverride, AssetTagsResponse } from '@/types';
import styles from './TranscriptPanel.module.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function msToTimestamp(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function parseTimestampInput(val: string): number | null {
  const trimmed = val.trim();
  if (!trimmed) return null;
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    return (parseInt(colonMatch[1], 10) * 60 + parseInt(colonMatch[2], 10)) * 1000;
  }
  const num = parseFloat(trimmed);
  return isNaN(num) ? null : Math.round(num * 1000);
}

// ─── Tag category config ──────────────────────────────────────────────────────

const TAG_CATEGORIES: { label: string; type: TagType; key: keyof AssetTagsResponse['tags'] }[] = [
  { label: 'Objects',     type: 'object',     key: 'objects'     },
  { label: 'Activities',  type: 'activity',   key: 'activities'  },
  { label: 'Scenes',      type: 'scene',      key: 'scenes'      },
  { label: 'Key Points',  type: 'key_point',  key: 'key_points'  },
  { label: 'AI Speakers', type: 'ai_speaker', key: 'ai_speakers' },
  { label: 'People',      type: 'person',     key: 'people'      },
  { label: 'Topics',      type: 'topic',      key: 'topics'      },
];

/* ─── Chapters row (commented out) ─────────────────────────────────────────────
interface ChaptersRowProps {
  chapters: Chapter[];
  currentSeconds: number;
  onSeek: (seconds: number) => void;
}

function ChaptersRow({ chapters, currentSeconds, onSeek }: ChaptersRowProps) {
  if (!chapters.length) return null;
  const activeIndex = chapters.reduce((best, ch, i) => {
    return ch.start_ms / 1000 <= currentSeconds ? i : best;
  }, 0);

  return (
    <div className={styles.chaptersRow} aria-label="Chapters">
      {chapters.map((ch, i) => (
        <button
          key={ch.index}
          className={`${styles.chapterBtn} ${i === activeIndex ? styles.chapterBtnActive : ''}`}
          onClick={() => onSeek(ch.start_ms / 1000)}
          title={ch.title}
        >
          <span className={styles.chapterTime}>{msToTimestamp(ch.start_ms)}</span>
          <span className={styles.chapterTitle}>{ch.title}</span>
        </button>
      ))}
    </div>
  );
}
─────────────────────────────────────────────────────────────────────────────── */

/* ─── Matched clips panel (commented out) ───────────────────────────────────────
interface MatchedClipsPanelProps {
  clips: MatchedClip[];
  moments: Moment[];
  onSeek: (seconds: number) => void;
}

function MatchedClipsPanel({ clips, moments, onSeek }: MatchedClipsPanelProps) {
  if (!clips.length && !moments.length) return null;

  return (
    <div className={styles.clipsPanel}>
      {clips.length > 0 && (
        <>
          <div className={styles.clipsPanelLabel}>Matched clips</div>
          {clips.map((clip, i) => (
            <div key={i} className={styles.clipCard}>
              <div className={styles.clipCardMeta}>
                <span className={styles.clipRange}>
                  {msToTimestamp(clip.start_ms)} – {msToTimestamp(clip.end_ms)}
                </span>
                <span className={styles.clipConfidence}>{clip.confidence}%</span>
                <button
                  className={styles.clipSeekBtn}
                  onClick={() => onSeek(clip.start_ms / 1000)}
                  aria-label={`Jump to ${msToTimestamp(clip.start_ms)}`}
                >
                  ▶ Jump
                </button>
              </div>
              {clip.snippets[0] && (
                <p className={styles.clipSnippet}>"{clip.snippets[0]}"</p>
              )}
            </div>
          ))}
        </>
      )}
      {moments.length > 0 && (
        <>
          <div className={styles.clipsPanelLabel}>Visual moments</div>
          <div className={styles.momentsRow}>
            {moments.map((m, i) => (
              <button
                key={i}
                className={styles.momentChip}
                onClick={() => m.timestamp_ms != null && onSeek(m.timestamp_ms / 1000)}
                disabled={m.timestamp_ms == null}
                title={m.text}
              >
                <span className={styles.momentType}>{m.type}</span>
                {m.timestamp_ms != null && (
                  <span className={styles.momentTime}>{msToTimestamp(m.timestamp_ms)}</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
─────────────────────────────────────────────────────────────────────────────── */

function TranscriptSkeleton() {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className={styles.skeletonLine}>
          <div className={styles.skeletonTime} />
          <div className={styles.skeletonBlock}>
            <div className={styles.skeletonText} style={{ width: `${60 + (i % 3) * 15}%` }} />
            <div className={styles.skeletonText} style={{ width: `${40 + (i % 2) * 20}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tags skeleton ────────────────────────────────────────────────────────────

function TagsSkeleton() {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={styles.skeletonTagCategory}>
          <div className={styles.skeletonText} style={{ width: '60px' }} />
          <div className={styles.skeletonChipsRow}>
            {Array.from({ length: 3 - (i % 2) }).map((_, j) => (
              <div key={j} className={styles.skeletonChip} style={{ width: `${40 + j * 18}px` }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tags tab ─────────────────────────────────────────────────────────────────

interface TagsTabProps {
  assetId: string;
}

function TagsTab({ assetId }: TagsTabProps) {
  const dispatch = useAppDispatch();
  const tagsData = useAppSelector((s) => s.tags.assetTags[assetId]);
  const tagsLoading = useAppSelector((s) => s.tags.loadingAssetId === assetId);
  const addLoading = useAppSelector((s) => s.tags.addLoading);
  const deleteLoading = useAppSelector((s) => s.tags.deleteLoading);

  const currentSeconds = useAppSelector((s) => s.player.currentSeconds);

  const [addingCategory, setAddingCategory] = useState<TagType | null>(null);
  const [addValue, setAddValue] = useState('');
  const [addStartMs, setAddStartMs] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editStartMs, setEditStartMs] = useState('');
  // Description-specific add form
  const [addingDesc, setAddingDesc] = useState(false);
  const [descValue, setDescValue] = useState('');
  const [descStartMs, setDescStartMs] = useState('');
  const [tagsSearch, setTagsSearch] = useState('');

  const handleAdd = async (type: TagType) => {
    const trimmed = addValue.trim();
    if (!trimmed) return;
    const startMs = parseTimestampInput(addStartMs);
    await dispatch(addAssetTag({
      assetId,
      payload: {
        tag_type: type, human_value: trimmed, source: 'manual',
        ...(startMs != null && { timestamp_ms_start: startMs }),
      },
    }));
    dispatch(fetchAssetTags(assetId));
    setAddValue('');
    setAddStartMs('');
    setAddingCategory(null);
  };

  const handleEditSave = async (tag: TagOverride) => {
    const trimmed = editValue.trim();
    const startMs = parseTimestampInput(editStartMs);
    const textChanged = trimmed !== tag.text;
    const tsChanged = startMs !== tag.timestamp_ms;
    if (!trimmed || (!textChanged && !tsChanged)) { setEditingId(null); return; }
    if (tag.id) await dispatch(deleteAssetTag({ assetId, tagId: tag.id }));
    await dispatch(addAssetTag({
      assetId,
      payload: {
        tag_type: tag.tag_type, human_value: trimmed, ai_value: tag.ai_value, source: 'human',
        ...(startMs != null && { timestamp_ms_start: startMs }),
      },
    }));
    dispatch(fetchAssetTags(assetId));
    setEditingId(null);
    setEditStartMs('');
  };

  const handleAddDesc = async () => {
    const trimmed = descValue.trim();
    if (!trimmed) return;
    const startMs = parseTimestampInput(descStartMs);
    await dispatch(addAssetTag({
      assetId,
      payload: {
        tag_type: 'description', human_value: trimmed, source: 'manual',
        ...(startMs != null && { timestamp_ms_start: startMs }),
      },
    }));
    dispatch(fetchAssetTags(assetId));
    setDescValue('');
    setDescStartMs('');
    setAddingDesc(false);
  };

  const startEdit = (tag: TagOverride, key: string) => {
    setEditingId(key);
    setEditValue(tag.text);
    setEditStartMs(tag.timestamp_ms != null ? msToTimestamp(tag.timestamp_ms) : msToTimestamp(currentSeconds * 1000));
    setAddingCategory(null);
  };

  if (tagsLoading && !tagsData) return <TagsSkeleton />;

  const descriptions: TagOverride[] = tagsData?.tags.descriptions ?? [];
  const hasAnyTags = descriptions.length > 0 || TAG_CATEGORIES.some(({ key }) => (tagsData?.tags[key] ?? []).length > 0);
  const filteredDescs = tagsSearch.trim()
    ? descriptions.filter(d => d.text.toLowerCase().includes(tagsSearch.toLowerCase()))
    : descriptions;

  return (
    <>
      <div className={styles.search}>
        <svg className={styles.searchIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          className={styles.searchInput}
          placeholder="Search tags..."
          value={tagsSearch}
          onChange={(e) => setTagsSearch(e.target.value)}
          disabled={!tagsData || !hasAnyTags}
          aria-label="Search tags"
        />
      </div>
    <div className={styles.tagsList}>
      {/* ── Description section — prose rows, not chips ── */}
      {(!tagsSearch.trim() || filteredDescs.length > 0 || addingDesc) && (
      <div className={`${styles.tagCategory} ${styles.descCategory}`}>
        <div className={styles.tagCategoryHead}>
          <span className={styles.tagCategoryLabel}>Description</span>
          <button
            className={styles.tagAddBtn}
            onClick={() => { if (!addingDesc) { setAddingDesc(true); setDescValue(''); setDescStartMs(msToTimestamp(currentSeconds * 1000)); } else { setAddingDesc(false); setDescValue(''); setDescStartMs(''); } }}
            aria-label="Add description"
          >+ Add</button>
        </div>

        <div className={styles.descRows}>
          {filteredDescs.map((d, i) => {
            const ts = d.timestamp_ms;
            const editKey = d.id ?? `desc-${i}`;
            const isEditingThis = editingId === editKey;

            if (isEditingThis) {
              return (
                <div key={editKey} className={styles.tagAddRow}>
                  <div className={styles.descAddTimestampRow}>
                    <span className={styles.tagTimestampLabel}>Timestamp (optional)</span>
                    <input
                      className={styles.tagTimestampInput}
                      placeholder="m:ss"
                      value={editStartMs}
                      onChange={(e) => setEditStartMs(e.target.value)}
                    />
                    <button
                      className={styles.tagTimestampCurrent}
                      onClick={() => setEditStartMs(msToTimestamp(currentSeconds * 1000))}
                      title="Use current time"
                    >↑</button>
                  </div>
                  <textarea
                    className={styles.descAddTextarea}
                    value={editValue}
                    rows={4}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(null); }}
                    autoFocus
                  />
                  <div className={styles.tagAddMain} style={{ justifyContent: 'flex-end' }}>
                    <button
                      className={styles.tagAddSave}
                      onClick={() => handleEditSave(d)}
                      disabled={addLoading || !editValue.trim()}
                    >{addLoading ? '…' : '✓'}</button>
                    <button
                      className={styles.tagAddCancel}
                      onClick={() => setEditingId(null)}
                    >✕</button>
                  </div>
                </div>
              );
            }

            return (
              <div key={editKey} className={styles.descRow}>
                {ts != null ? (
                  <span
                    className={styles.descRowTimestamp}
                    onClick={() => dispatch(seekToSeconds(ts / 1000))}
                    role="button"
                    title="Jump to timestamp"
                  >{msToTimestamp(ts)}</span>
                ) : (
                  <span className={styles.descRowTimestampEmpty} />
                )}
                <p className={styles.descRowText}>{d.text}</p>
                <button
                  className={styles.descRowEdit}
                  onClick={() => { startEdit(d, editKey); setAddingDesc(false); }}
                  aria-label="Edit description"
                  title="Edit"
                >✎</button>
                {d.id && (
                  <button
                    className={styles.descRowDelete}
                    onClick={() => dispatch(deleteAssetTag({ assetId, tagId: d.id! }))}
                    aria-label="Remove description"
                    title="Remove"
                  >×</button>
                )}
              </div>
            );
          })}
          {descriptions.length === 0 && !addingDesc && (
            <span className={styles.tagEmpty}>No description yet</span>
          )}
        </div>

        {addingDesc && (
          <div className={styles.tagAddRow}>
            <div className={styles.descAddTimestampRow}>
              <span className={styles.tagTimestampLabel}>Timestamp (optional)</span>
              <input
                className={styles.tagTimestampInput}
                placeholder="m:ss"
                value={descStartMs}
                onChange={(e) => setDescStartMs(e.target.value)}
              />
              <button
                className={styles.tagTimestampCurrent}
                onClick={() => setDescStartMs(msToTimestamp(currentSeconds * 1000))}
                title="Use current time"
              >↑</button>
            </div>
            <textarea
              className={styles.descAddTextarea}
              placeholder="Describe this video…"
              value={descValue}
              rows={4}
              onChange={(e) => setDescValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setAddingDesc(false); setDescValue(''); setDescStartMs(''); } }}
              autoFocus
            />
            <div className={styles.tagAddMain} style={{ justifyContent: 'flex-end' }}>
              <button
                className={styles.tagAddSave}
                onClick={handleAddDesc}
                disabled={addLoading || !descValue.trim()}
                aria-label="Save description"
              >{addLoading ? '…' : '✓'}</button>
              <button
                className={styles.tagAddCancel}
                onClick={() => { setAddingDesc(false); setDescValue(''); setDescStartMs(''); }}
                aria-label="Cancel"
              >✕</button>
            </div>
          </div>
        )}
      </div>
      )}
      {TAG_CATEGORIES.map(({ label, type, key }) => {
        const allTags: TagOverride[] = tagsData?.tags[key] ?? [];
        const tags = tagsSearch.trim()
          ? allTags.filter(t => t.text.toLowerCase().includes(tagsSearch.toLowerCase()))
          : allTags;
        const isAdding = addingCategory === type;
        if (tagsSearch.trim() && tags.length === 0 && !isAdding) return null;

        return (
          <div key={type} className={styles.tagCategory}>
            <div className={styles.tagCategoryHead}>
              <span className={styles.tagCategoryLabel}>{label}</span>
              <button
                className={styles.tagAddBtn}
                onClick={() => { if (!isAdding) { setAddingCategory(type); setAddValue(''); setAddStartMs(msToTimestamp(currentSeconds * 1000)); setEditingId(null); } else { setAddingCategory(null); setAddValue(''); setAddStartMs(''); } }}
                aria-label={`Add ${label} tag`}
              >
                + Add
              </button>
            </div>

            <div className={styles.tagChipsRow}>
              {tags.map((tag, tagIdx) => {
                const tagKey = tag.id ?? `ai-${type}-${tagIdx}`;
                const isDeleting = !!(tag.id && deleteLoading[tag.id]);

                if (editingId === tagKey) {
                  return (
                    <div key={tagKey} className={styles.tagEditInline}>
                      <div className={styles.tagEditMain}>
                        <input
                          className={styles.tagEditInput}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(tag); if (e.key === 'Escape') { setEditingId(null); setEditStartMs(''); } }}
                          autoFocus
                        />
                        <button className={styles.tagEditSave} onClick={() => handleEditSave(tag)} aria-label="Save edit">✓</button>
                        <button className={styles.tagEditCancel} onClick={() => { setEditingId(null); setEditStartMs(''); }} aria-label="Cancel edit">✕</button>
                      </div>
                      <div className={styles.tagTimestampRow}>
                        <span className={styles.tagTimestampLabel}>Start</span>
                        <input className={styles.tagTimestampInput} placeholder="m:ss" value={editStartMs} onChange={(e) => setEditStartMs(e.target.value)} />
                        <button className={styles.tagTimestampCurrent} onClick={() => setEditStartMs(msToTimestamp(currentSeconds * 1000))} title="Use current time">↑</button>
                      </div>
                    </div>
                  );
                }

                const isAiTag = !tag.id;
                const showDiff = !isAiTag && tag.status === 'edited' && !!tag.ai_value && tag.ai_value !== tag.text;

                return (
                  <div key={tagKey} className={`${styles.tagChip} ${isAiTag ? styles.tagChipAi : ''} ${isDeleting ? styles.tagChipDeleting : ''}`}>
                    {tag.timestamp_ms != null && (
                      <span
                        className={styles.tagChipTime}
                        onClick={() => dispatch(seekToSeconds(tag.timestamp_ms! / 1000))}
                        role="button"
                        title="Jump to timestamp"
                      >{msToTimestamp(tag.timestamp_ms)}</span>
                    )}
                    <span
                      className={styles.tagChipText}
                      onClick={tag.timestamp_ms != null && !isDeleting ? () => dispatch(seekToSeconds(tag.timestamp_ms! / 1000)) : undefined}
                      title={tag.text}
                      role={tag.timestamp_ms != null ? 'button' : undefined}
                    >
                      {tag.text}
                    </span>
                    {showDiff && (
                      <span
                        className={styles.tagChipWas}
                        title={`AI original: ${tag.ai_value}`}
                      >≈</span>
                    )}
                    {isAiTag ? (
                      <button
                        className={styles.tagChipOverride}
                        onClick={() => startEdit(tag, tagKey)}
                        title="Override this AI tag"
                        aria-label={`Override ${tag.text}`}
                      >✎</button>
                    ) : (
                      <>
                        <button
                          className={styles.tagChipOverride}
                          onClick={() => !isDeleting && startEdit(tag, tagKey)}
                          disabled={isDeleting}
                          title="Edit tag"
                          aria-label={`Edit ${tag.text}`}
                        >✎</button>
                        <button
                          className={styles.tagChipDelete}
                          onClick={() => dispatch(deleteAssetTag({ assetId, tagId: tag.id! }))}
                          disabled={isDeleting}
                          aria-label={`Remove ${tag.text}`}
                        >
                          {isDeleting ? '…' : '×'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}

              {tags.length === 0 && !isAdding && (
                <span className={styles.tagEmpty}>No tags yet</span>
              )}
            </div>

            {isAdding && (
              <div className={styles.tagAddRow}>
                <div className={styles.tagAddMain}>
                  <input
                    className={styles.tagAddInput}
                    placeholder={`Add ${label.toLowerCase()} tag…`}
                    value={addValue}
                    onChange={(e) => setAddValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(type); if (e.key === 'Escape') { setAddingCategory(null); setAddStartMs(''); } }}
                    autoFocus
                  />
                  <button
                    className={styles.tagAddSave}
                    onClick={() => handleAdd(type)}
                    disabled={addLoading || !addValue.trim()}
                    aria-label="Save tag"
                  >
                    {addLoading ? '…' : '✓'}
                  </button>
                  <button className={styles.tagAddCancel} onClick={() => { setAddingCategory(null); setAddStartMs(''); }} aria-label="Cancel">✕</button>
                </div>
                <div className={styles.tagTimestampRow}>
                  <span className={styles.tagTimestampLabel}>Start</span>
                  <input className={styles.tagTimestampInput} placeholder="m:ss" value={addStartMs} onChange={(e) => setAddStartMs(e.target.value)} />
                  <button className={styles.tagTimestampCurrent} onClick={() => setAddStartMs(msToTimestamp(currentSeconds * 1000))} title="Use current time">↑</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
    </>
  );
}



export default function TranscriptPanel() {
  const dispatch = useAppDispatch();
  const currentSeconds = useAppSelector((s) => s.player.currentSeconds);
  const search = useAppSelector((s) => s.ui.transcriptSearch);
  const activeAsset = useAppSelector((s) => s.search.activeAsset);
  const activeAssetLoading = useAppSelector((s) => s.search.activeAssetLoading);
  const activeRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<'transcript' | 'tags'>('transcript');

  // Eagerly fetch tags whenever a new video is selected so data is ready
  // before the user even opens the Tags tab.
  useEffect(() => {
    if (activeAsset?.asset_id) {
      dispatch(fetchAssetTags(activeAsset.asset_id));
    }
  }, [activeAsset?.asset_id, dispatch]);

  // Transcript lines come only from the `transcript` array.
  // matched_segments are scene/content descriptions, not word-level transcripts —
  // do not use them as a substitute for a real transcript.
  const hasAsset = !!activeAsset;
  const hasTranscript = !!activeAsset?.transcript?.length;

  const transcriptLines: TranscriptLine[] = useMemo(() => {
    if (activeAsset?.transcript?.length) {
      return activeAsset.transcript.map((phrase, i) => ({
        id: `phrase-${i}`,
        timestamp: formatTimestamp(Math.floor(phrase.start_ms / 1000)),
        seconds: phrase.start_ms / 1000,
        speaker: phrase.speaker,
        text: phrase.text,
      }));
    }
    // No asset loaded yet → show mock lines for the default demo video
    if (!hasAsset) return defaultTranscriptLines;
    // Asset loaded but transcript is empty
    return [];
  }, [activeAsset, hasAsset]);

  // Derive active line from current playback position
  const activeId = useMemo(() => {
    let found = transcriptLines[0]?.id ?? null;
    for (const line of transcriptLines) {
      if (line.seconds <= currentSeconds) found = line.id;
      else break;
    }
    return found;
  }, [currentSeconds]);

  // Scroll active line into view as video plays
  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeId]);

  const filteredLines = search
    ? transcriptLines.filter((l) =>
        l.text.toLowerCase().includes(search.toLowerCase()) ||
        l.speaker.toLowerCase().includes(search.toLowerCase())
      )
    : transcriptLines;

  const speakerCount = useMemo(() => {
    const speakers = new Set(transcriptLines.map((l) => l.speaker));
    return speakers.size;
  }, [transcriptLines]);

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <h4 className={styles.headTitle}>
          {activeTab === 'transcript' ? 'Interactive Transcript' : 'Insights'}
        </h4>
        {activeTab === 'transcript' && (
          activeAssetLoading ? (
            <span className={styles.headMeta}>Loading…</span>
          ) : hasAsset && !hasTranscript ? (
            <span className={styles.headMeta}>No transcript</span>
          ) : (
            <span className={styles.headMeta}>{speakerCount} speaker{speakerCount !== 1 ? 's' : ''} · {transcriptLines.length} segments</span>
          )
        )}
      </div>

      {/* Tab bar */}
      <div className={styles.tabs} role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'transcript'}
          className={`${styles.tab} ${activeTab === 'transcript' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('transcript')}
        >
          Transcript
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'tags'}
          className={`${styles.tab} ${activeTab === 'tags' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('tags')}
        >
          Insights
        </button>
      </div>

      {/* ── Transcript tab ── */}
      {activeTab === 'transcript' && (
        <div className={styles.tabContent}>
          <div className={styles.search}>
            <svg className={styles.searchIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              className={styles.searchInput}
              placeholder="Search within this video..."
              value={search}
              onChange={(e) => dispatch(setTranscriptSearch(e.target.value))}
              aria-label="Search transcript"
              disabled={activeAssetLoading || (hasAsset && !hasTranscript)}
            />
          </div>

          {activeAssetLoading ? (
            <TranscriptSkeleton />
          ) : hasAsset && !hasTranscript ? (
            <>
              {/* <ChaptersRow chapters={activeAsset?.chapters ?? []} currentSeconds={currentSeconds} onSeek={(s) => dispatch(seekToSeconds(s))} /> */}
              {/* <MatchedClipsPanel clips={activeAsset?.matched_clips ?? []} moments={activeAsset?.matched_moments ?? []} onSeek={(s) => dispatch(seekToSeconds(s))} /> */}
              <div className={styles.empty} role="status">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <p className={styles.emptyTitle}>No transcript available</p>
              </div>
            </>
          ) : (
            <>
              {/* <ChaptersRow chapters={activeAsset?.chapters ?? []} currentSeconds={currentSeconds} onSeek={(s) => dispatch(seekToSeconds(s))} /> */}
              {/* <MatchedClipsPanel clips={activeAsset?.matched_clips ?? []} moments={activeAsset?.matched_moments ?? []} onSeek={(s) => dispatch(seekToSeconds(s))} /> */}
              <div className={styles.list} role="list" aria-label="Video transcript">
                {filteredLines.map((line) => {
                  const isActive = line.id === activeId;
                  return (
                    <div
                      key={line.id}
                      ref={isActive ? activeRef : undefined}
                      role="listitem"
                      className={`${styles.line} ${isActive ? styles.lineActive : ''}`}
                      onClick={() => dispatch(seekToSeconds(line.seconds))}
                    >
                      <span className={styles.lineTime}>{line.timestamp}</span>
                      <div>
                        <div className={styles.lineSpeaker}>{line.speaker}</div>
                        <div className={styles.lineText} dangerouslySetInnerHTML={{ __html: line.text }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className={styles.foot}>
            <span>Click any sentence to jump the video</span>
          </div>
        </div>
      )}

      {/* ── Tags tab ── */}
      {activeTab === 'tags' && (
        <div className={styles.tabContent}>
          {activeAsset?.asset_id ? (
            <TagsTab assetId={activeAsset.asset_id} />
          ) : (
            <div className={styles.empty} role="status">
              <p className={styles.emptyTitle}>Select a video to view tags</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
